import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { authedPost } from '@/lib/api';
import { BUDGET_ENTRY_BATCH_MAX } from '@releve/shared';

export interface BudgetCategoryEntry {
  id: string;
  ledger_entry_id: string;
  budget_category_id: string;
  organization_id: string;
  created_at: string;
}

/** Fetch all budget category tags for a given ledger entry */
export const useBudgetCategoryEntriesByLedgerEntry = (ledgerEntryId?: string) => {
  return useQuery({
    queryKey: ['budgetCategoryEntries', 'byEntry', ledgerEntryId],
    queryFn: async () => {
      if (!ledgerEntryId) return [];
      const { data, error } = await supabase
        .from('budget_category_entries')
        .select('*, budget_category:budget_categories(id, name, budget_id, group_id)')
        .eq('ledger_entry_id', ledgerEntryId);
      if (error) throw error;
      return data as (BudgetCategoryEntry & { budget_category: { id: string; name: string; budget_id: string; group_id: string } })[];
    },
    enabled: !!ledgerEntryId,
  });
};

/** Fetch all budget category tags for entries in a transaction */
export const useBudgetCategoryEntriesByTransaction = (transactionId?: string) => {
  return useQuery({
    queryKey: ['budgetCategoryEntries', 'byTransaction', transactionId],
    queryFn: async () => {
      if (!transactionId) return [];

      // Get all ledger entries for this transaction, then their budget tags
      const { data: entries, error: entriesError } = await supabase
        .from('ledger_entries')
        .select('id')
        .eq('transaction_id', transactionId);

      if (entriesError) throw entriesError;
      if (!entries?.length) return [];

      const entryIds = entries.map((e: { id: string }) => e.id);
      const { data, error } = await supabase
        .from('budget_category_entries')
        .select('*, budget_category:budget_categories(id, name, budget_id, group_id)')
        .in('ledger_entry_id', entryIds);

      if (error) throw error;
      return data as (BudgetCategoryEntry & { budget_category: { id: string; name: string; budget_id: string; group_id: string } })[];
    },
    enabled: !!transactionId,
  });
};

// Writes go through the Hono backend (Pattern B, /api/v1/budgets — see
// apps/api/src/routes/accounting/budgets.ts), which checks both ids against
// the org; the reads above stay direct for now.
const entriesPath = (organizationId: string) =>
  `/api/v1/budgets/orgs/${organizationId}/category-entries`;

/**
 * Split into request-sized slices: the API caps each call at
 * BUDGET_ENTRY_BATCH_MAX, and a large journal can carry more entries than
 * that. Chunks are sent sequentially by the callers.
 */
const chunksOf = <T>(items: T[]): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += BUDGET_ENTRY_BATCH_MAX) {
    out.push(items.slice(i, i + BUDGET_ENTRY_BATCH_MAX));
  }
  return out;
};

/** Bulk save budget category tags for entries after transaction creation/update */
export const useSaveBudgetCategoryEntries = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      entries,
      organizationId,
    }: {
      entries: { ledger_entry_id: string; budget_category_id: string }[];
      organizationId: string;
    }) => {
      if (!entries.length) return [];

      const saved: BudgetCategoryEntry[] = [];
      for (const chunk of chunksOf(entries)) {
        const result = await authedPost<{ entries: BudgetCategoryEntry[] }>(entriesPath(organizationId), {
          entries: chunk.map((e) => ({
            ledger_entry_id: e.ledger_entry_id,
            budget_category_id: e.budget_category_id,
          })),
        });
        saved.push(...result.entries);
      }
      return saved;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgetCategoryEntries'] });
      queryClient.invalidateQueries({ queryKey: ['budgetMonthView'] });
      queryClient.invalidateQueries({ queryKey: ['budgetToBudget'] });
      queryClient.invalidateQueries({ queryKey: ['budgetTotalView'] });
      queryClient.invalidateQueries({ queryKey: ['budgetSpendByMonth'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

/** Delete all budget category tags for a set of ledger entries (used when updating a transaction) */
export const useDeleteBudgetCategoryEntriesByLedgerEntries = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ledgerEntryIds,
      organizationId,
    }: {
      ledgerEntryIds: string[];
      organizationId: string;
    }) => {
      if (!ledgerEntryIds.length) return;
      for (const chunk of chunksOf(ledgerEntryIds)) {
        await authedPost<{ deleted: number }>(`${entriesPath(organizationId)}/delete`, {
          ledger_entry_ids: chunk,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgetCategoryEntries'] });
      queryClient.invalidateQueries({ queryKey: ['budgetMonthView'] });
      queryClient.invalidateQueries({ queryKey: ['budgetToBudget'] });
      queryClient.invalidateQueries({ queryKey: ['budgetTotalView'] });
      queryClient.invalidateQueries({ queryKey: ['budgetSpendByMonth'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};
